import React, { useEffect, useImperativeHandle } from 'react';
import { CustomIcon } from 'components/CustomIcon';
import MBAutocomplete from 'components/Forms/MBAutocomplete/MBAutocomplete';
import { FormikErrors, FormikProps, FormikTouched, useFormik } from 'formik';
import { cloneDeep } from 'lodash';
import { Checkbox } from './Checkbox';
import * as Styled from './ContactRepModalView.styles';
import { getValidationSchema } from './Form.validation';
import { Option } from '../../types';

export interface ContactRepFormState {
  representative: Option;
  message: string;
  pricingRequest: boolean;
  productLeadTime: boolean;
  requestMeeting: boolean;
  installationImagery: boolean;
  other: boolean;
}

interface ContactRepFormProps {
  representatives: Option[];
  onSubmit: (values: ContactRepFormState) => Promise<void>;
  isFormSentInProgress: boolean;
  defaultSelectedRepresentative?: Option;
}

enum Field {
  REPRESENTATIVE = 'representative',
  MESSAGE = 'message',
  REQUEST_ERROR = 'requestError',
  PRICING_REQUEST = 'pricingRequest',
  PRODUCT_LEAD_TIME = 'productLeadTime',
  REQUEST_MEETING = 'requestMeeting',
  INSTALLATION_IMAGERY = 'installationImagery',
  OTHER = 'other',
}

export const getDefaultInitialValues = (
  props: Pick<ContactRepFormProps, 'defaultSelectedRepresentative'>,
) => {
  // TODO: refactor without type assertion, or add comment
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return {
    [Field.REPRESENTATIVE]: props.defaultSelectedRepresentative,
  } as ContactRepFormState;
};

const isOptionEqualToValue = (option: Option, state: Option) =>
  option.value === state.value;
const getIsValidUtil =
  (
    touched: FormikTouched<ContactRepFormState>,
    errors: FormikErrors<ContactRepFormState>,
  ) =>
  (field: Field) =>
    // @ts-expect-error // TODO: fix types
    touched[field] != null && typeof errors[field] === 'string';

export const Form = React.forwardRef<
  FormikProps<ContactRepFormState>,
  ContactRepFormProps
>((props, ref) => {
  const { representatives, defaultSelectedRepresentative, onSubmit } = props;
  const [isTextareaOpen, setIsTextareaOpen] = React.useState<boolean>(false);
  const [isFormDisabled, setIsFormDisabled] = React.useState<boolean>(true);

  useEffect(() => {
    if (defaultSelectedRepresentative && !props.isFormSentInProgress) {
      setIsFormDisabled(false);
    } else {
      setIsFormDisabled(true);
    }

    return () => setIsFormDisabled(true);
  }, [defaultSelectedRepresentative, props.isFormSentInProgress]);

  const form = useFormik<ContactRepFormState>({
    initialValues: cloneDeep(getDefaultInitialValues(props)),
    validationSchema: getValidationSchema(),
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        await onSubmit?.(values).then(() => {
          form.resetForm({ values: getDefaultInitialValues(props) });
        });
      } catch (e) {
        await form.setFieldValue(
          Field.REQUEST_ERROR,
          e?.message || 'Something went wrong',
        );
      }
    },
    innerRef: ref,
  });

  useImperativeHandle(ref, () => form);

  const { errors, getFieldProps, setFieldValue, touched } = form;
  const isValid = getIsValidUtil(touched, errors);

  return (
    <Styled.ContactRepModalBody>
      <MBAutocomplete
        {...getFieldProps(Field.REPRESENTATIVE)}
        onChange={(_, value) => setFieldValue(Field.REPRESENTATIVE, value)}
        variant="standard"
        options={representatives}
        isOptionEqualToValue={isOptionEqualToValue}
        // @ts-expect-error // TODO: fix types
        placeholder="Rep Name"
        fullWidth
        required
        size="small"
        disablePortal={true}
        disabled={isFormDisabled}
        value={getFieldProps(Field.REPRESENTATIVE).value ?? ''}
      />
      <Styled.ContactRepModalCheckboxGroup>
        <Checkbox
          title="Request Pricing"
          checked={false}
          disabled={isFormDisabled}
          {...getFieldProps(Field.PRICING_REQUEST)}
          onChange={(isChecked) => {
            setFieldValue(Field.PRICING_REQUEST, isChecked);
          }}
        />
        <Checkbox
          title="Request Lead Time"
          checked={false}
          disabled={isFormDisabled}
          {...getFieldProps(Field.PRODUCT_LEAD_TIME)}
          onChange={(isChecked) => {
            setFieldValue(Field.PRODUCT_LEAD_TIME, isChecked);
          }}
        />
        <Checkbox
          title="Request Meeting"
          checked={false}
          disabled={isFormDisabled}
          {...getFieldProps(Field.REQUEST_MEETING)}
          onChange={(isChecked) => {
            setFieldValue(Field.REQUEST_MEETING, isChecked);
          }}
        />
        <Checkbox
          title="Request Install Shots"
          checked={false}
          disabled={isFormDisabled}
          {...getFieldProps(Field.INSTALLATION_IMAGERY)}
          onChange={(isChecked) => {
            setFieldValue(Field.INSTALLATION_IMAGERY, isChecked);
          }}
        />
        <Checkbox
          title="Other"
          checked={false}
          disabled={isFormDisabled}
          {...getFieldProps(Field.OTHER)}
          onChange={(isChecked) => {
            setFieldValue(Field.OTHER, isChecked);
          }}
        />
      </Styled.ContactRepModalCheckboxGroup>

      {!isTextareaOpen ? (
        <Styled.AddMessageButton
          variant="text"
          size="small"
          onClick={() => setIsTextareaOpen(true)}
          disabled={isFormDisabled}
          dense
        >
          <CustomIcon name="plus" dense />
          Add a message
        </Styled.AddMessageButton>
      ) : (
        <Styled.ContactRepModalTextField
          label="Add a message"
          fullWidth
          multiline
          variant="standard"
          size="small"
          focused={true}
          maxRows={2}
          disabled={isFormDisabled}
          error={isValid(Field.MESSAGE)}
          {...getFieldProps(Field.MESSAGE)}
        />
      )}
    </Styled.ContactRepModalBody>
  );
});

Form.displayName = 'ContactRepForm';
